import { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { fmtCurrency } from '../lib/localeUtils';
import {
  ShieldAlert, Loader2, AlertTriangle, CheckCircle2, Search,
  TrendingUp, Eye, AlertOctagon, ShieldCheck, DollarSign,
  ShoppingCart, RotateCcw, CreditCard, Package, Truck, Building2,
  Shield, Server, Banknote
} from 'lucide-react';
import { keyed } from '../lib/listKeys';

const SEVERITY_STYLES = {
  critical: 'bg-red-500/15 border-red-500/40 text-red-600',
  high: 'bg-orange-500/15 border-orange-500/40 text-orange-600',
  medium: 'bg-yellow-500/15 border-yellow-500/40 text-yellow-700',
  low: 'bg-blue-500/15 border-blue-500/40 text-blue-600',
};

const CATEGORY_LABELS = {
  sales_manipulation: 'Sales Manipulation',
  discount_abuse: 'Discount Abuse',
  return_fraud: 'Return Fraud',
  inventory_shrinkage: 'Inventory Shrinkage',
  procurement_fraud: 'Procurement Fraud',
  payment_irregularity: 'Payment Irregularity',
  insurance_fraud: 'Insurance Fraud',
  corporate_fraud: 'Corporate Fraud',
  system_tampering: 'System Tampering',
  data_integrity: 'Data Integrity',
  time_anomaly: 'Time Anomaly',
  user_anomaly: 'User Anomaly',
  other: 'Other',
};

const INVESTIGATION_AREAS = [
  { id: 'sales', label: 'Sales', icon: ShoppingCart },
  { id: 'returns', label: 'Returns', icon: RotateCcw },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'discounts', label: 'Discounts', icon: Banknote },
  { id: 'inventory', label: 'Inventory', icon: Package },
  { id: 'procurement', label: 'Procurement', icon: Truck },
  { id: 'suppliers', label: 'Suppliers', icon: Truck },
  { id: 'insurance', label: 'Insurance', icon: Shield },
  { id: 'corporate', label: 'Corporate', icon: Building2 },
  { id: 'system_audit', label: 'System Audit', icon: Server },
];

export default function FraudAnalysisPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedAreas, setSelectedAreas] = useState(INVESTIGATION_AREAS.map(a => a.id));

  const toggleArea = (id) => {
    setSelectedAreas(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedAreas(INVESTIGATION_AREAS.map(a => a.id));
  const selectNone = () => setSelectedAreas([]);

  const runAnalysis = async () => {
    if (selectedAreas.length === 0) {
      toast.error('Select at least one investigation area');
      return;
    }
    setLoading(true);
    try {
      const body = { areas: selectedAreas };
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
      const res = await api.post('/ai/fraud-analysis', body);
      setReport(res.data);
      toast.success('Fraud analysis complete');
    } catch (err) {
      const { handleAIError } = await import('../lib/aiErrorHandler');
      handleAIError(err, 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    if (level === 'critical' || level === 'high') return 'text-red-500';
    if (level === 'moderate') return 'text-orange-500';
    if (level === 'low') return 'text-green-500';
    return 'text-muted-foreground';
  };

  const getRiskBg = (level) => {
    if (level === 'critical' || level === 'high') return 'bg-red-500/10 border-red-500/30';
    if (level === 'moderate') return 'bg-orange-500/10 border-orange-500/30';
    if (level === 'low') return 'bg-green-500/10 border-green-500/30';
    return 'bg-muted/50';
  };

  const fraudValue = report?.analysis?.estimated_fraud_value || 0;

  return (
    <div className="space-y-6" data-testid="fraud-analysis-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShieldAlert className="h-6 w-6 text-orange-500" /> Fraud Detection
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">AI-powered forensic analysis across all business operations</p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Date Range */}
          <div className="flex flex-col sm:flex-row items-end gap-3">
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="fraud-date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="fraud-date-to" />
              </div>
            </div>
            <Button onClick={runAnalysis} disabled={loading || selectedAreas.length === 0} className="gap-2 h-10 shrink-0" data-testid="run-analysis-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {loading ? 'Analyzing...' : 'Run Analysis'}
            </Button>
          </div>

          <Separator />

          {/* Investigation Areas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Investigation Areas</Label>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-[10px] text-primary hover:underline" data-testid="select-all-areas">Select All</button>
                <button onClick={selectNone} className="text-[10px] text-muted-foreground hover:underline" data-testid="select-none-areas">Clear</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {INVESTIGATION_AREAS.map(area => {
                const Icon = area.icon;
                const checked = selectedAreas.includes(area.id);
                return (
                  <label
                    key={area.id}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${checked ? 'bg-primary/5 border-primary/40' : 'bg-muted/30 border-transparent hover:bg-muted/50'}`}
                    data-testid={`area-${area.id}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggleArea(area.id)} />
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs truncate">{area.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground">
            {dateFrom || dateTo ? `Period: ${dateFrom || 'start'} to ${dateTo || 'now'}` : 'Default: last 30 days'} | {selectedAreas.length} area{selectedAreas.length !== 1 ? 's' : ''} selected
          </p>
        </CardContent>
      </Card>

      {!report && !loading && (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <ShieldAlert className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <h3 className="text-lg font-semibold">No Analysis Run Yet</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Select investigation areas, set a date range, then click "Run Analysis".
            </p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="py-16 text-center">
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
            <h3 className="text-lg font-semibold">Analyzing {selectedAreas.length} Areas...</h3>
            <p className="text-sm text-muted-foreground mt-1">AI is scanning for fraud patterns, anomalies, and system tampering.</p>
          </CardContent>
        </Card>
      )}

      {report && !loading && (
        <>
          {/* Risk Overview + Fraud Value */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className={`border ${getRiskBg(report.analysis?.risk_level)}`}>
              <CardContent className="p-4 text-center">
                {report.analysis?.risk_level === 'critical' || report.analysis?.risk_level === 'high'
                  ? <AlertOctagon className="h-8 w-8 mx-auto mb-2 text-red-500" />
                  : report.analysis?.risk_level === 'moderate'
                  ? <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                  : <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-green-500" />}
                <p className={`text-2xl font-bold capitalize ${getRiskColor(report.analysis?.risk_level)}`}>
                  {report.analysis?.risk_level || 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Risk Level</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{report.analysis?.risk_score ?? 0}<span className="text-sm font-normal text-muted-foreground">/100</span></p>
                <p className="text-xs text-muted-foreground mt-1">Risk Score</p>
              </CardContent>
            </Card>
            {/* Estimated Fraud Value — Highlighted */}
            <Card className="border-red-500/30 bg-red-500/5">
              <CardContent className="p-4 text-center">
                <DollarSign className="h-6 w-6 mx-auto mb-1 text-red-500" />
                <p className="text-2xl font-bold text-red-500">{fmtCurrency(fraudValue)}</p>
                <p className="text-xs text-muted-foreground mt-1">Est. Fraud Value</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{report.analysis?.flags?.length || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Flags Raised</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{report.data_summary?.total_sales || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Transactions</p>
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <Card>
            <CardContent className="p-4">
              <p className="text-sm">{report.analysis?.summary}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {report.areas_investigated?.map(a => (
                  <Badge key={a} variant="secondary" className="text-[9px]">{INVESTIGATION_AREAS.find(x => x.id === a)?.label || a}</Badge>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Generated: {new Date(report.generated_at).toLocaleString()} by {report.generated_by} | {report.data_summary?.period}
              </p>
            </CardContent>
          </Card>

          {/* Fraud Value Breakdown */}
          {report.analysis?.estimated_fraud_breakdown?.length > 0 && (
            <Card className="border-red-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-red-500" /> Fraud Value Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {keyed(report.analysis.estimated_fraud_breakdown, b => b.area).map(({ item: b, key }, i) => (
                    <div key={key} className="flex items-center justify-between p-2.5 bg-red-500/5 rounded-lg border border-red-500/10" data-testid={`fraud-breakdown-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{b.area}</p>
                        <p className="text-xs text-muted-foreground truncate">{b.description}</p>
                      </div>
                      <div className="text-right ml-3 shrink-0">
                        <p className="text-sm font-bold text-red-500">{fmtCurrency(b.estimated_loss || 0)}</p>
                        <Badge variant="outline" className="text-[8px] capitalize">{b.confidence} confidence</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Data Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {keyed([
              { label: 'Revenue', value: fmtCurrency(report.data_summary?.total_revenue || 0) },
              { label: 'Discounts', value: fmtCurrency(report.data_summary?.total_discounts || 0), color: 'text-orange-500' },
              { label: 'Returns', value: `${report.data_summary?.total_returns || 0}`, extra: fmtCurrency(report.data_summary?.total_returns_amount || 0), color: 'text-red-500' },
              { label: 'Sales', value: report.data_summary?.total_sales || 0 },
              { label: 'POs', value: report.data_summary?.purchase_orders || 0 },
              { label: 'Removals', value: report.data_summary?.expired_removals || 0 },
              { label: 'Audit Logs', value: report.data_summary?.system_logs || 0 },
              { label: 'Period', value: report.data_summary?.period || '30d' },
            ], entry => entry.label).map(({ item, key }) => (
              <div key={key} className="p-2 bg-muted/50 rounded-lg text-center">
                <p className="text-[10px] text-muted-foreground">{item.label}</p>
                <p className={`text-xs font-bold ${item.color || ''}`}>{item.value}</p>
                {item.extra && <p className={`text-[9px] ${item.color || ''}`}>{item.extra}</p>}
              </div>
            ))}
          </div>

          {/* Flags */}
          {report.analysis?.flags?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" /> Flags ({report.analysis.flags.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {keyed(report.analysis.flags, flag => `${flag.title}-${flag.severity}`).map(({ item: flag, key }, i) => (
                    <div key={key} className={`p-3 rounded-lg border ${SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.low}`} data-testid={`flag-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[9px] capitalize">{flag.severity}</Badge>
                            <Badge variant="secondary" className="text-[9px]">{CATEGORY_LABELS[flag.category] || flag.category}</Badge>
                            {flag.estimated_value > 0 && (
                              <Badge className="text-[9px] bg-red-500/10 text-red-600 border-red-500/30" variant="outline">
                                {fmtCurrency(flag.estimated_value)}
                              </Badge>
                            )}
                          </div>
                          <p className="font-semibold text-sm mt-1.5">{flag.title}</p>
                          <p className="text-xs mt-1 opacity-80">{flag.description}</p>
                        </div>
                      </div>
                      {flag.affected_items?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {keyed(flag.affected_items.slice(0, 6)).map(({ item, key }) => (
                            <Badge key={key} variant="outline" className="text-[8px] font-mono">{item}</Badge>
                          ))}
                          {flag.affected_items.length > 6 && <Badge variant="outline" className="text-[8px]">+{flag.affected_items.length - 6}</Badge>}
                        </div>
                      )}
                      {flag.recommended_action && (
                        <p className="text-[10px] mt-2 font-medium opacity-70">Action: {flag.recommended_action}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Patterns */}
          {report.analysis?.patterns?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4" /> Detected Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {keyed(report.analysis.patterns, p => p.type).map(({ item: p, key }, i) => (
                    <div key={key} className="p-3 bg-muted/50 rounded-lg" data-testid={`pattern-${i}`}>
                      <p className="text-sm font-semibold">{p.type}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                      {p.evidence && <p className="text-[10px] text-muted-foreground mt-1 italic">Evidence: {p.evidence}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations + Clean Areas */}
          <div className="grid lg:grid-cols-2 gap-4">
            {report.analysis?.recommendations?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" /> Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {keyed(report.analysis.recommendations).map(({ item: r, key }, i) => (
                      <li key={key} className="flex items-start gap-2 text-sm">
                        <span className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {report.analysis?.clean_areas?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" /> Clean Areas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {keyed(report.analysis.clean_areas).map(({ item: a, key }) => (
                      <li key={key} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
